// Supply-side revenue, pivoted: what each screen and media owner earned.
//
// The mirror image of the revenue pivot. That screen cuts the company's
// revenue by who SPENT it (campaign, ad set, advertiser); this one cuts the
// same rupees by who EARNED them — the screen, and the media owner it belongs
// to — with the mobility that produced them (rides, trips, hours, km) beside
// the money.
//
// SAME ARCHITECTURE AS Pivot.js, DELIBERATELY. URL is the state, the server
// does every sum, the table renders the shape the payload echoed rather than
// the shape in the URL, and the client assembles rows into a tree without
// ever adding two numbers together. The route (/api/insights/screens) returns
// the same envelope as /api/insights/pivot — vocabulary, rows, totals,
// facets, source, parity, rollup_agrees, truncated — plus one block of its
// own: cross_mart, a day-by-day tie between this mart and the advertiser
// one, rendered on the face of the screen exactly like the parity check. The
// same rupees must appear on both sides of the market or one of them is
// lying.
//
// ONE HONEST LIMITATION, STATED RATHER THAN HIDDEN: media-owner attribution
// is by CURRENT owner. No ownership history exists anywhere in the schema, so
// a screen transferred between owners carries its whole history to the new
// one. Saying so on the screen beats letting someone discover it in a
// dispute.
//
// Styling: Pivot.module.css is dataset-agnostic (layout, table, strips —
// nothing advertiser-shaped), so this screen imports it rather than forking a
// copy that would drift.

import React, { useMemo, useCallback, useState, useEffect, useRef } from 'react';
import { NavLink, useSearchParams } from 'react-router-dom';
import {
  RefreshCw, Download, SlidersHorizontal, Search, X, Info, ChevronDown, ChevronRight,
} from 'lucide-react';
import { Loading, ErrorState, Pill } from '../../components/ui';
import useApi from '../../lib/useApi';
import api from '../../../services/api';
import Filters from './Filters';
import DateRange, { toISO, addDays, startOfDay } from './DateRange';
import CustomizePanel from './CustomizePanel';
import s from './Pivot.module.css';

const SUB = 'The supply side of the same revenue: what each screen and media owner earned, with '
  + 'the rides, trips and hours behind it. Reads the screens mart through the same gate mirror as '
  + 'the advertiser pivot, and ties itself to that pivot day by day on every request — the result '
  + 'is in the status line below. Media-owner attribution is by CURRENT owner: no ownership '
  + 'history exists, so a transferred screen carries its whole history to its new owner.';

const DIM_LABEL = {
  media_tenant: 'Media owner', screen: 'Screen',
  day: 'Day', week: 'Week', month: 'Month', hour: 'Hour of day',
};
const DIM_HELP = {
  media_tenant: 'The media owner the screen belongs to NOW. Attribution is by current owner — no ownership history exists, so a transferred screen’s whole history moves with it.',
  screen: 'One row per screen, grouped on the device id so a rename stays one row. The label is the thing name.',
  day: 'IST calendar day, the bucket billing and the signals plane both use.',
  week: 'IST week, starting Monday (date_trunc).',
  month: 'IST calendar month.',
  hour: 'Hour of the IST day, 0–23. Stored as the raw hour so any daypart scheme can be applied later without rebuilding the mart.',
};
const MEA_LABEL = {
  spend: 'Earned ₹ (presence-billed from 1 Aug 2026)', spend_all: 'Earned ₹ (all plays)', plays: 'Plays', billable: 'Billable',
  occupied: 'With passenger', empty: 'Without passenger', vision_out: 'Vision out',
  awaiting: 'Awaiting verdict', pre_gate: 'Pre-gate (unmeasured era)', refused: 'Refused (never billed)',
  rides: 'Rides', trips: 'Trips', travel_hours: 'Driving hours', presence_hours: 'Passenger hours',
  km: 'Km', cpm: 'CPM', occupancy_rate: 'Occupancy',
  spend_occupied: 'Earned ₹ · passenger-verified · incl. retro-derived presence',
  live_occupied: 'Earned ₹ · live-occupied',
  cpm_verified: 'CPM · passenger-verified',
};
const MEA_HELP = {
  spend: 'Rupees priced by the regime that governed each day: the billing predicate before 1 Aug 2026, presence-verified money only from that day on — INCLUDING retro-derived presence for 1–28 Aug 2026, marked º, because the live verdict column does not exist before the gate cutover. The billing predicate passes ALL pre-cutover money, which reads as a false comfort on August days — this blend does not. Converted from paise at the source.',
  spend_all: 'Rupees over ALL plays, ignoring the gate. The gap against Earned ₹ is what the presence regime withholds — not revenue falling.',
  plays: 'Creative plays on the screen, summed from impression_count — never a count of batches.',
  billable: 'Plays counting towards an advertiser under billing’s own gate. An outcome, not a bucket.',
  occupied: 'Plays with a passenger verified aboard.',
  empty: 'Plays verdicted with nobody aboard.',
  vision_out: 'Plays while the camera pipeline was out — no verdict was possible.',
  awaiting: 'Plays whose presence verdict has not landed yet.',
  pre_gate: 'Plays from before presence measurement existed. An unmeasured era, kept in its own bucket rather than guessed at.',
  refused: 'Plays the presence gate refused to bill — no passenger evidence. Never billed.',
  rides: 'Rides the cab carried in this row’s bucket.',
  trips: 'Trips overlapping this row’s bucket.',
  travel_hours: 'Hours the vehicle spent driving.',
  presence_hours: 'Hours with a passenger verified aboard.',
  km: 'Kilometres travelled.',
  cpm: 'Earned over ADVERTISER-FACING impressions × 1000, recomputed from each row’s own sums — a subtotal is not the average of its children.',
  occupancy_rate: 'Share of verdicted plays with a passenger aboard, from this row’s own sums — never averaged from child rows.',
  spend_occupied: 'Money earned on passenger-verified impressions INCLUDING retro-derived presence: pre-decree rows show values reconstructed from island evidence, marked º where it survives and — where it does not. Retro-derived verdicts in displays remain an open founder ruling — labelled here, never blended silently. On fully gated days it equals Earned ₹.',
  live_occupied: 'Money on LIVE presence verdicts only (verdict = occupied), with no retro reconstruction. It is NOT the media-console figure — since 11 Sep that console carries the retro arm too, so this is the diagnostic showing how much of a window the live verdict column actually covers.',
  cpm_verified: 'What a passenger-verified impression actually earned: verified ₹ per 1000 verified impressions, recomputed from this row’s own sums.',
};

const DIM_SECTIONS = [
  { id: 'level', label: 'Level', keys: ['media_tenant', 'screen'] },
  { id: 'time', label: 'Time', keys: ['day', 'week', 'month', 'hour'] },
];
const MEA_SECTIONS = [
  { id: 'money', label: 'Money', keys: ['spend', 'spend_all', 'cpm'] },
  { id: 'verified', label: 'If only passengers counted', keys: ['occupied', 'spend_occupied', 'live_occupied', 'cpm_verified', 'occupancy_rate'] },
  { id: 'presence', label: 'Presence detail', keys: ['plays', 'billable', 'empty', 'vision_out', 'awaiting', 'pre_gate', 'refused'] },
  { id: 'mobility', label: 'Mobility', keys: ['rides', 'trips', 'travel_hours', 'presence_hours', 'km'] },
];

const VIEWS = [
  { id: 'pivot', label: 'Pivot', help: 'Rows nest, with a subtotal at every breakdown level. Subtotals are computed by the query, not summed from the rows below them.' },
  { id: 'flat', label: 'Ungrouped', help: 'One row per combination of breakdowns, with no subtotals.' },
];

const n = (v) => (v == null ? '—' : Number(v).toLocaleString('en-IN'));
const inr = (v, dp = 2) => (v == null ? '—'
  : `₹${Number(v).toLocaleString('en-IN', { minimumFractionDigits: dp, maximumFractionDigits: dp })}`);

// The presence family is MASKED on rows whose plays are wholly pre-decree:
// presence was never measured there (server flag `measured:false`, V328), so a
// zero would be a claim, not a fact. awaiting/pre_gate stay numeric — they ARE
// the explanation the reader needs.
const UNMEASURED_MASK = new Set(['occupied', 'empty', 'vision_out', 'occupancy_rate', 'spend_occupied', 'live_occupied', 'cpm_verified']);

// WHICH KEYS WEAR THE º, which is NOT the same question as which render '—'.
//
// `spend` began carrying retro-derived money on 11 Sep, and it is the DEFAULT
// money column and the default sort key — so without this it was the one place
// retro money appeared with no mark at all, one line below a tooltip promising
// "labelled here, never blended silently". It cannot join UNMEASURED_MASK
// instead: pre-epoch `spend` is as-billed money that IS known, and masking a
// July row to '—' would hide a real figure to fix a labelling bug.
//
// occupancy_rate is deliberately ABSENT: screenPivot computes it from
// sum(m.occupied)/sum(m.empty) with no rp term, so it contains no retro and
// a º on it would be a false claim in the other direction.
const RETRO_MARK = new Set(['occupied', 'empty', 'spend', 'spend_occupied']);

const RETRO_TIP = 'Pre-gate era, derived retroactively from presence evidence '
  + '(island algorithm, 100.000% fidelity vs 20,496 live verdicts). Analytics '
  + 'only — billing was and stays untouched.';

const fmtCell = (key, v, measured, retro) => {
  if (measured === false && UNMEASURED_MASK.has(key)) return '—';
  // live_occupied never wears the retro mark: it is definitionally
  // live-verdict money only, and on a retro row its (usually zero) value is
  // genuinely live.
  if (retro === true && UNMEASURED_MASK.has(key) && key !== 'live_occupied' && v != null) {
    const base = key === 'spend_occupied' ? inr(v)
      : key === 'occupancy_rate' ? `${v}%` : n(v);
    return `${base} º`;
  }
  if (v == null) return '—';
  if (key === 'spend' || key === 'spend_all' || key === 'cpm' || key === 'spend_occupied' || key === 'live_occupied' || key === 'cpm_verified') return inr(v);
  if (key === 'occupancy_rate') return `${v}%`;
  if (key === 'travel_hours' || key === 'presence_hours' || key === 'km') {
    return Number(v).toLocaleString('en-IN', { maximumFractionDigits: 1 });
  }
  return n(v);
};

const fmtDay = (iso) => {
  const [y, m, d] = String(iso || '').split('-').map(Number);
  return y && m && d
    ? new Date(y, m - 1, d).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })
    : '—';
};
const fmtClock = (ts) => (ts
  ? new Date(ts).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' })
  : 'never');

const fmtKey = (dim, v) => {
  if (v == null) return null;
  if (dim === 'hour') return `${String(v).padStart(2, '0')}:00`;
  if (dim === 'day' || dim === 'week' || dim === 'month') return String(v).slice(0, 10);
  return String(v);
};

function InfoDot({ title }) {
  return (
    <span className={s.dot} tabIndex={0} role="img" aria-label={title} title={title}>
      <Info size={11} aria-hidden="true" />
    </span>
  );
}

// ── the tree ────────────────────────────────────────────────────────────────
//
// ASSEMBLY ONLY, as in Pivot.js: every number came from the server; the client
// nests rows and orders them and never adds two of them together. Rows arrive
// ordered by depth ascending, so a parent is always in the map before its
// children look for it.
function buildTree(rows, dims) {
  const pathAt = (r, d) => dims.slice(0, d).map((k) => `${k}=${r.keys[k] ?? ' '}`).join('/');
  const byPath = new Map();
  const roots = [];
  for (const r of rows) {
    const depth = r.depth == null ? dims.length : r.depth;
    const path = pathAt(r, depth);
    // `leaf` is DERIVED, never trusted from the payload: a row at full depth is
    // a leaf by definition, and a missing flag must not make one read as a
    // subtotal.
    const node = { ...r, depth, path, leaf: depth >= dims.length, children: [] };
    byPath.set(path, node);
    const parent = depth > 1 ? byPath.get(pathAt(r, depth - 1)) : null;
    (parent ? parent.children : roots).push(node);
  }
  return roots;
}

function flattenTree(nodes, collapsed, out = []) {
  for (const node of nodes) {
    out.push(node);
    if (node.children.length && !collapsed.has(node.path)) flattenTree(node.children, collapsed, out);
  }
  return out;
}

export default function Screens() {
  const [params, setParams] = useSearchParams();
  const readList = useCallback(
    (k) => (params.get(k) ? params.get(k).split(',').filter(Boolean) : []), [params]);

  const today = useMemo(() => startOfDay(new Date()), []);
  const range = useMemo(() => ({
    from: params.get('from') || toISO(addDays(today, -29)),
    to: params.get('to') || toISO(today),
  }), [params, today]);

  const breakdowns = useMemo(() => {
    const b = readList('breakdowns');
    return b.length ? b : ['media_tenant'];
  }, [readList]);
  const measures = useMemo(() => {
    const m = readList('measures');
    return m.length ? m : ['spend', 'plays', 'occupied', 'rides', 'travel_hours'];
  }, [readList]);
  const filters = useMemo(() => ({
    media_tenants: readList('media_tenants'),
    screens: readList('screens'),
  }), [readList]);
  const q = params.get('q') || '';
  const view = params.get('view') === 'flat' ? 'flat' : 'pivot';

  const patch = useCallback((obj) => {
    const p = new URLSearchParams(params);
    for (const [k, v] of Object.entries(obj)) {
      if (Array.isArray(v)) { if (v.length) p.set(k, v.join(',')); else p.delete(k); }
      else if (v) p.set(k, v); else p.delete(k);
    }
    setParams(p, { replace: true });
  }, [params, setParams]);

  const qs = useMemo(() => {
    const p = new URLSearchParams({
      from: range.from, to: range.to, view,
      breakdowns: breakdowns.join(','), measures: measures.join(','),
    });
    for (const k of ['media_tenants', 'screens']) {
      if (filters[k].length) p.set(k, filters[k].join(','));
    }
    if (q) p.set('q', q);
    return p.toString();
  }, [range, breakdowns, measures, filters, q, view]);

  const [customizeOpen, setCustomizeOpen] = useState(false);
  const [customizeTab, setCustomizeTab] = useState('breakdowns');
  const [collapsed, setCollapsed] = useState(() => new Set());

  // Collapse state is keyed by a path built from the breakdowns; changing them
  // makes every stored path meaningless, so it is dropped.
  const shape = `${breakdowns.join(',')}|${view}`;
  useEffect(() => { setCollapsed(new Set()); }, [shape]);

  // The search box is a DRAFT that debounces into the URL — every keystroke
  // going straight to the query param would fire an aggregate per character.
  const [draft, setDraft] = useState(q);
  const lastCommitted = useRef(q);
  useEffect(() => {
    // Only accept the URL as the source of truth when it changed from
    // elsewhere (back button, a pasted link) — otherwise our own debounce
    // would fight the field the user is typing in.
    if (q !== lastCommitted.current) { lastCommitted.current = q; setDraft(q); }
  }, [q]);
  useEffect(() => {
    if (draft.trim() === q) return undefined;
    const t = setTimeout(() => { lastCommitted.current = draft.trim(); patch({ q: draft.trim() }); }, 300);
    return () => clearTimeout(t);
  }, [draft, q, patch]);

  const { data, error, loading, reload } = useApi(
    () => api.get(`/insights/screens?${qs}`).then((r) => r.data?.data), [qs]);

  // The chrome stays mounted across refetches — unmounting would steal focus
  // from the search box on the very keystroke that triggered the request.
  const first = loading && !data;
  const vocabulary = data?.vocabulary;
  const dimensions = useMemo(
    () => (vocabulary?.dimensions || []).map((d) => ({ key: d.key, label: DIM_LABEL[d.key] || d.label })),
    [vocabulary],
  );
  const measureDefs = useMemo(
    () => (vocabulary?.measures || []).map((m) => ({ key: m.key, label: MEA_LABEL[m.key] || m.label })),
    [vocabulary],
  );

  const toggle = (list, v, key, max) => {
    const next = list.includes(v) ? list.filter((x) => x !== v) : [...list, v];
    if (max && next.length > max) return;
    if (!next.length) return;               // never leave the pivot with no axis
    patch({ [key]: next });
  };

  // THE TABLE RENDERS THE SHAPE THE SERVER ECHOED, NOT THE SHAPE IN THE URL —
  // they differ for the length of a refetch, and during a deploy window an
  // older API echoes no view and its rows carry no depth or leaf. The payload
  // decides; no echo means flat. See the long comments in Pivot.js — the
  // defect class was found there, against a server that had not been
  // redeployed yet.
  const viewBreakdowns = data?.breakdowns?.length ? data.breakdowns : breakdowns;
  const viewMeasures = data?.measures?.length ? data.measures : measures;
  const viewMode = data ? (data.view || 'flat') : view;

  const rows = useMemo(() => data?.rows || [], [data]);
  const visible = useMemo(() => {
    if (!rows.length) return [];
    if (viewMode === 'flat') return rows.map((r, i) => ({ ...r, path: `f${i}`, children: [] }));
    return flattenTree(buildTree(rows, viewBreakdowns), collapsed);
  }, [rows, viewBreakdowns, viewMode, collapsed]);

  const toggleNode = (path) => setCollapsed((prev) => {
    const next = new Set(prev);
    if (next.has(path)) next.delete(path); else next.add(path);
    return next;
  });

  // CSV of exactly what the server returned — no re-aggregation, no rounding,
  // no rates recomputed in the browser. Subtotal rows are exported too, marked
  // by a Level column.
  const exportCsv = () => {
    if (!data?.rows) return;
    const cell = (v) => {
      if (v == null) return '';
      const t = String(v);
      return /[",\n]/.test(t) ? `"${t.replace(/"/g, '""')}"` : t;
    };
    // A labelled dimension exports BOTH its name and its id: the name is what
    // a reader recognises, the id survives a rename and pastes back into a
    // filter.
    const labelled = viewBreakdowns.filter((b) => data.rows.some((r) => r.labels?.[b]));
    const keyCols = viewBreakdowns.flatMap((b) => (labelled.includes(b)
      ? [DIM_LABEL[b] || b, `${DIM_LABEL[b] || b} id`] : [DIM_LABEL[b] || b]));
    const keyVals = (r) => viewBreakdowns.flatMap((b) => {
      const has = Object.prototype.hasOwnProperty.call(r.keys, b);
      const v = !has ? '' : (r.keys[b] == null ? 'unassigned' : r.keys[b]);
      return labelled.includes(b) ? [has ? (r.labels?.[b] || '') : '', v] : [v];
    });
    const lead = viewMode === 'pivot' ? ['Level'] : [];
    const leadVals = (r) => (viewMode === 'pivot'
      ? [r.leaf ? 'row' : `subtotal:${DIM_LABEL[viewBreakdowns[r.depth - 1]] || viewBreakdowns[r.depth - 1]}`]
      : []);
    const cols = [...lead, ...keyCols,
      ...viewMeasures.map((m) => measureDefs.find((x) => x.key === m)?.label || MEA_LABEL[m] || m)];
    const lines = [cols.map(cell).join(',')];
    for (const r of data.rows) {
      lines.push([...leadVals(r), ...keyVals(r), ...viewMeasures.map((m) => r[m])].map(cell).join(','));
    }
    lines.push([...[...lead, ...keyCols].map((_, i) => (i ? '' : 'TOTAL (all matching rows)')),
      ...viewMeasures.map((m) => data.totals?.[m])].map(cell).join(','));
    if (data.truncated) {
      lines.push(cell(`rows truncated at ${vocabulary?.max_rows}; the TOTAL line is over every matching row`));
    }
    const blob = new Blob([lines.join('\n')], { type: 'text/csv;charset=utf-8' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = `screens-pivot_${range.from}_${range.to}.csv`;
    document.body.appendChild(a); a.click(); a.remove();
    setTimeout(() => URL.revokeObjectURL(a.href), 0);
  };

  const totals = data?.totals || {};
  const parity = data?.parity;
  const crossMart = data?.cross_mart;
  const crossBad = crossMart && Number(crossMart.days_disagreeing) > 0;
  const facets = data?.facets;
  const truncated = data?.truncated;
  const source = data?.source;
  const colCount = viewBreakdowns.length + viewMeasures.length;

  // The facet vocabulary is this screen's own — Filters is dataset-agnostic
  // and renders whatever descriptors it is handed.
  const filterFields = useMemo(() => (facets ? [
    { key: 'media_tenants', label: 'Media owner', chipNoun: 'owner',
      options: (facets.media_tenants || []).map((t) => ({ value: t.id, label: t.name || t.id })) },
    { key: 'screens', label: 'Screen', chipNoun: 'screen',
      options: (facets.screens || []).map((sc) => ({ value: sc.id, label: sc.thing_name || sc.id })) },
  ] : null), [facets]);

  return (
    <div className={s.page} data-fill>
      {/* ── row 1: identity, view, actions ────────────────────────────── */}
      <header className={s.head}>
        <h1 className={s.h1}>Screen earnings</h1>
        <InfoDot title={SUB} />

        <div className={s.viewToggle} role="group" aria-label="Table view">
          {VIEWS.map((v) => (
            <button key={v.id} type="button" title={v.help}
                    aria-pressed={viewMode === v.id}
                    className={`${s.viewBtn} ${viewMode === v.id ? s.viewBtnOn : ''}`}
                    onClick={() => patch({ view: v.id === 'pivot' ? '' : v.id })}>
              {v.label}
            </button>
          ))}
        </div>

        <span className={s.spacer} />

        <button type="button" className={s.btn} onClick={reload} disabled={loading}
                aria-busy={loading}
                title="Re-run the query. The mart itself refreshes hourly, so this re-reads it rather than re-deriving it.">
          <RefreshCw size={13} className={loading ? s.spin : undefined} aria-hidden="true" />
          Refresh
        </button>
        <button type="button" className={s.btn} onClick={exportCsv} disabled={!rows.length}
                title="Download these rows plus the server's totals as CSV. Values are exported exactly as returned — nothing is recomputed in the browser.">
          <Download size={13} aria-hidden="true" />
          Export
        </button>
        <button type="button" aria-pressed={customizeOpen}
                className={`${s.btn} ${customizeOpen ? s.btnOn : ''}`}
                onClick={() => setCustomizeOpen((o) => !o)}
                title="Choose which breakdowns nest the rows and which measures form the columns.">
          <SlidersHorizontal size={13} aria-hidden="true" />
          Customize
        </button>
      </header>

      {/* ── row 2: search, filters, window ────────────────────────────── */}
      <div className={s.controls}>
        <div className={s.searchWrap}>
          <Search size={13} className={s.searchIcon} aria-hidden="true" />
          <input
            type="search" className={s.searchInput} value={draft}
            onChange={(e) => setDraft(e.target.value)}
            placeholder="Search screen or owner"
            aria-label="Search rows"
            title="Filters on the SERVER, so the totals and the parity check move with it. Matches screen thing name, screen id and media owner."
          />
          {draft && (
            <button type="button" className={s.searchClear} aria-label="Clear search"
                    onClick={() => setDraft('')}>
              <X size={12} />
            </button>
          )}
        </div>

        {facets && filterFields && (
          <Filters compact facets={facets} fields={filterFields} value={filters}
                   onChange={(v) => patch(v)} summary={null} />
        )}

        <span className={s.spacer} />
        <DateRange from={range.from} to={range.to}
                   onChange={(f, t) => patch({ from: f, to: t })} />
      </div>

      {/* STOP CONDITIONS sit above the table; the quiet confirmations live in
          the strip below it. Same doctrine as Pivot.js. */}
      {parity && !parity.in_parity && (
        <div className={s.parityBad}>
          <Pill tone="bad">OUT OF PARITY</Pill>
          <span className={s.parityText}>
            These numbers DISAGREE with the live derivation on closed days:
            {' '}&Delta;{n(parity.plays?.delta)} plays, &Delta;{inr(parity.spend?.delta)},
            {' '}&Delta;{n(parity.billable?.delta)} advertiser-facing. Do not use this view
            until it is resolved.
          </span>
        </div>
      )}
      {crossBad && (
        <div className={s.parityBad}>
          <Pill tone="bad">DISAGREES WITH ADVERTISER PIVOT</Pill>
          <span className={s.parityText}>
            The screens mart and the advertiser mart disagree on {n(crossMart.days_disagreeing)} of
            {' '}{n(crossMart.days_checked)} days checked. The same rupees must appear on both sides
            of the market; do not use this view until it is resolved.
          </span>
        </div>
      )}
      {data?.rollup_agrees === false && (
        <div className={s.parityBad}>
          <Pill tone="bad">SUBTOTALS DISAGREE</Pill>
          <span className={s.parityText}>
            The grouped query&rsquo;s grand total does not match the ungrouped one over the
            same rows. Switch to Ungrouped and report this.
          </span>
        </div>
      )}
      {truncated && (
        <div className={s.truncated}>
          Showing the first {n(vocabulary?.max_rows)} rows. The totals row is computed over
          EVERY matching row, not the ones displayed, so it stays correct — add a filter or
          drop a breakdown to see them all.
        </div>
      )}

      {/* ── the frame: table left, customize right ────────────────────── */}
      <div className={s.body}>
        <div className={s.main}>
          <div className={`${s.tableWrap} ${loading && data ? s.stale : ''}`}>
            {first && <Loading what="screen earnings" />}
            {!first && error && <ErrorState error={error} onRetry={reload} />}
            {!first && !error && (
              <table className={s.table}>
                <thead>
                  <tr>
                    {viewBreakdowns.map((b) => (
                      <th key={b} className={s.hKey}>
                        {DIM_LABEL[b] || b}
                        <InfoDot title={DIM_HELP[b]} />
                      </th>
                    ))}
                    {viewMeasures.map((m) => (
                      <th key={m} className={s.hNum}>
                        {measureDefs.find((x) => x.key === m)?.label || MEA_LABEL[m] || m}
                        <InfoDot title={MEA_HELP[m]} />
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {visible.map((node) => {
                    const isSub = viewMode === 'pivot' && !node.leaf;
                    const open = !collapsed.has(node.path);
                    return (
                      <tr key={node.path} className={isSub ? s.subRow : undefined}>
                        {viewBreakdowns.map((b, i) => {
                          const show = viewMode === 'flat' || i === node.depth - 1;
                          if (!show) return <td key={b} className={s.key} />;
                          const label = node.labels?.[b] || fmtKey(b, node.keys[b]);
                          const id = node.keys[b];
                          return (
                            <th key={b} scope="row" className={s.key}
                                title={node.labels?.[b] ? `${node.labels[b]} · ${id}` : undefined}>
                              {/* Nesting is by column, not pixel padding — see
                                  Pivot.js. Only the chevron gutter needs
                                  aligning. */}
                              <span className={s.keyBody}>
                                {viewMode === 'pivot' && node.children.length > 0 ? (
                                  <button type="button" className={s.chev}
                                          aria-expanded={open}
                                          aria-label={`${open ? 'Collapse' : 'Expand'} ${label || 'unassigned'}`}
                                          onClick={() => toggleNode(node.path)}>
                                    {open ? <ChevronDown size={12} /> : <ChevronRight size={12} />}
                                  </button>
                                ) : viewMode === 'pivot' && i === 0 && viewBreakdowns.length > 1
                                  ? <span className={s.chevGap} /> : null}
                                {label == null
                                  ? <span className={s.null} title="Earnings whose screen no longer resolves — kept rather than dropped, so the totals stay whole.">unassigned</span>
                                  : <span className={s.keyName}>{label}</span>}
                                {node.labels?.[b] && id != null && (
                                  <span className={s.keyId}>{String(id).slice(0, 8)}</span>
                                )}
                              </span>
                            </th>
                          );
                        })}
                        {viewMeasures.map((m) => (
                          <td key={m} className={s.num}
                              title={node.measured === false && UNMEASURED_MASK.has(m)
                                ? 'Pre-gate era: presence was not measured for these plays — the value is unknowable, not zero.'
                                : (node.retro === true && RETRO_MARK.has(m) ? RETRO_TIP : undefined)}>
                            {fmtCell(m, node[m], node.measured, node.retro)}
                          </td>
                        ))}
                      </tr>
                    );
                  })}
                  {!visible.length && (
                    <tr><td colSpan={colCount} className={s.empty}>
                      {q
                        ? <>Nothing in this window matches &ldquo;{q}&rdquo;.{' '}
                          <button type="button" className={s.linkBtn} onClick={() => setDraft('')}>
                            Clear the search
                          </button> to see every row.</>
                        : 'Nothing matches this window and these filters.'}
                    </td></tr>
                  )}
                </tbody>
                <tfoot>
                  <tr>
                    <th colSpan={viewBreakdowns.length} scope="row" className={s.totalLabel}>
                      Total
                      <InfoDot title="Computed over every matching row by its own query — not summed from the rows on screen, so it stays correct even when the view is truncated or collapsed." />
                    </th>
                    {viewMeasures.map((m) => (
                      <td key={m} className={`${s.num} ${s.totalCell}`}>{fmtCell(m, totals[m], totals.measured, totals.retro)}</td>
                    ))}
                  </tr>
                </tfoot>
              </table>
            )}
          </div>

          {/* STATUS STRIP — window, what reconciled, the cross-mart tie, and
              when the numbers were last derived. */}
          {data && (
            <div className={s.strip}>
              <span title="Every bucket on this screen is an IST calendar bucket, the same one billing settles on.">
                {fmtDay(range.from)} – {fmtDay(range.to)} · Indian Standard Time
              </span>
              <span title="Rows currently rendered, out of what the query returned. Collapsing a group hides rows here and changes nothing below.">
                {n(visible.length)} of {n(rows.length)}{truncated ? '+' : ''} rows ·
                {' '}{viewBreakdowns.length} breakdown{viewBreakdowns.length > 1 ? 's' : ''} ·
                {' '}{viewMeasures.length} measure{viewMeasures.length > 1 ? 's' : ''}
              </span>
              {data.regime && (
                <span title="Earned ₹ is priced by the regime that governed each day: the billing predicate before 1 Aug 2026, presence-verified money from it. Earned ₹ (all plays) and the parity check stay blanket — books are never filtered.">
                  {data.regime.note}
                </span>
              )}
              {parity?.in_parity && (
                <span className={s.ok}
                      title="Closed days in this window match the live derivation exactly — same plays, same rupees, same advertiser-facing count (blanket accrual on both sides — the display regime never filters books). Checked on every request, not on a schedule.">
                  Reconciled · {n(parity.plays?.mart)} plays, {inr(parity.spend?.mart)} on closed days
                </span>
              )}
              {crossMart && !crossBad && (
                <span className={s.ok}
                      title="Day by day, the supply-side total equals the advertiser pivot's total over the same closed days — the same rupees appear on both sides of the market. Checked on every request.">
                  Ties to advertiser pivot: {n(crossMart.days_checked)} days checked, 0 disagreeing
                </span>
              )}
              {parity?.open_day && (parity.open_day.plays !== 0 || parity.open_day.spend !== 0) && (
                <span title="Today is still accruing, so a materialised table necessarily trails the live source between refreshes. This amount IS included in the rows and totals above, and excluded from the parity assertion — a property of materialisation, not a defect.">
                  Today +{n(parity.open_day.plays)} plays · {inr(parity.open_day.spend)}
                </span>
              )}
              <span className={`${s.updated} ${source?.stale ? s.staleText : ''}`}
                    title={source?.stale
                      ? 'The hourly refresh has not run in over six hours — four missed runs. These numbers are stale; treat them as a floor.'
                      : 'When the mart was last refreshed, not when this page loaded. The refresh runs hourly.'}>
                {source?.stale ? 'STALE · ' : ''}Updated {fmtClock(source?.refreshed_at)}
              </span>
            </div>
          )}
        </div>

        {customizeOpen && vocabulary && (
          <CustomizePanel
            tab={customizeTab}
            onTab={setCustomizeTab}
            dimensions={dimensions}
            measures={measureDefs}
            dimensionSections={DIM_SECTIONS}
            measureSections={MEA_SECTIONS}
            selectedBreakdowns={breakdowns}
            selectedMeasures={measures}
            onToggleBreakdown={(k) => toggle(breakdowns, k, 'breakdowns', vocabulary.max_breakdowns)}
            onToggleMeasure={(k) => toggle(measures, k, 'measures')}
            onClose={() => setCustomizeOpen(false)}
            maxBreakdowns={vocabulary.max_breakdowns}
            help={{ ...DIM_HELP, ...MEA_HELP }}
            footNote={(
              <>
                Earned, before GST; media-owner attribution is by current owner; rates recomputed
                from each row&rsquo;s own sums.{' '}
                <NavLink to="/v2/insights/pivot" className={s.link}>Revenue pivot</NavLink>
              </>
            )}
          />
        )}
      </div>
    </div>
  );
}
